import React from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { X, Clock, Sun } from 'lucide-react';

import { EPresence, EPresenceDuration } from 'src/api/users-api';
import { useSetPresence } from 'src/services/api/users/presence';

import styles from 'src/components/layout/landlord/SetAwayConfirmationModal.module.css';

type Props = {
    onClose: () => void;
};

type DurationOption = {
    duration: EPresenceDuration;
    icon: React.ReactNode;
    label: string;
    sublabel: string;
    testId: string;
};

const OPTIONS: DurationOption[] = [
    {
        duration: EPresenceDuration.ONE_HOUR,
        icon: <Clock size={22} />,
        label: 'For 1 hour',
        sublabel: 'Reverts automatically after 60 minutes',
        testId: 'set-away-one-hour',
    },
    {
        duration: EPresenceDuration.REST_OF_DAY,
        icon: <Sun size={22} />,
        label: 'For the rest of the day',
        sublabel: 'Resets at your local midnight',
        testId: 'set-away-rest-of-day',
    },
];

export default function SetAwayConfirmationModal({ onClose }: Props) {
    const { mutateAsync, isLoading } = useSetPresence();

    const setAway = async (duration: EPresenceDuration) => {
        try {
            await mutateAsync({ presence: EPresence.AWAY, duration });
            onClose();
        } catch {
            // Error notification is handled by useSetPresence's onError callback.
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            PaperProps={{ className: styles.paper }}
        >
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.awayDot} />
                    <Typography variant="body1" className={styles.headerTitle}>
                        Set yourself away
                    </Typography>
                </div>
                <IconButton size="small" onClick={onClose} disabled={isLoading} className={styles.closeButton}>
                    <X size={18} />
                </IconButton>
            </div>

            <div className={styles.subtitle}>
                <Typography variant="body2" color="text.secondary">
                    While away, teammates see you as unavailable and can pick up your leads. How long?
                </Typography>
            </div>

            <div className={styles.options}>
                {OPTIONS.map(({ duration, icon, label, sublabel, testId }) => (
                    <ButtonBase
                        key={duration}
                        onClick={() => setAway(duration)}
                        disabled={isLoading}
                        data-testid={testId}
                        className={styles.optionButton}
                    >
                        <div className={styles.optionIconWrap}>
                            {icon}
                        </div>
                        <div className={styles.optionTextWrap}>
                            <Typography variant="body2" className={styles.optionLabel}>
                                {label}
                            </Typography>
                            <Typography variant="caption" className={styles.optionSublabel}>
                                {sublabel}
                            </Typography>
                        </div>
                    </ButtonBase>
                ))}
            </div>

            <div className={styles.cancelWrap}>
                <Button
                    fullWidth
                    variant="text"
                    color="inherit"
                    onClick={onClose}
                    disabled={isLoading}
                    className={styles.cancelButton}
                >
                    Cancel
                </Button>
            </div>
        </Dialog>
    );
}
